import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { RubleSymbol } from '@r1-frontend/ui-react/experimental/purchase/cost/Cost/RubleSymbol';
import { pluralize } from '@r1-frontend/shared/helpers/pluralize';
import { bonusPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import { getIsAbnLoyaltyProgramEnabled } from '~/src/features/domru-bonus/constants/abnVariants';
import { selectCardPaySum } from '~/src/features/payments-abn/components/SeasonTicketPayment/store/selectors/selectPaymentSum';
import { TState } from '~/src/store';

interface IPopupData {
    type: EResultPopupStatus,
    title: string,
    text?: string,
    isNeedSupport?: boolean,
    isBonusesPaymentSuccessfull?: boolean,
}

const defaultPopupData: IPopupData = {
    type: EResultPopupStatus.DEFAULT,
    title: '',
};

export const usePaymentResult = (): IPopupData => {
    const {
        auth: { clientPersonal },
        payment: { paymentData: { paymentSum, bonusesValue, isUseBonuses, isConnectAutoPay }, result },
    } = useSelector((state: TState) => state);

    const cardPaymentSum = useSelector(selectCardPaySum);
    const bonuses = useMemo(
        () => getIsAbnLoyaltyProgramEnabled() ? bonusesValue * 0.2 : bonusesValue,
        [bonusesValue],
    );

    const { payStatus, isPayCardSuccess, isBonusesSuccess, isAutoPaySuccess, queryAgreement } = result;
    const { agreement } = clientPersonal || {};
    const bonusesWithText = `${bonusesValue} ${pluralize(bonuses, bonusPluralize)}`;

    const setPaymentResultByStatus = useCallback((): IPopupData => {
        if (payStatus === 1) {
            const text = (bonuses && isUseBonuses)
                ? isBonusesSuccess
                    ? `Баланс пополнен на ${paymentSum} ${RubleSymbol}. Списали с бонусного счёта ${bonusesWithText}.`
                    : `Баланс пополнен на ${cardPaymentSum} ${RubleSymbol}. Но нам не удалось списать с бонусного счёта ${bonusesWithText}. `
                : '';

            return {
                type: EResultPopupStatus.SUCCESS,
                title: `Оплата по договору ${queryAgreement || agreement} прошла`,
                text,
                isNeedSupport: (isUseBonuses && !isBonusesSuccess),
            };
        } else {
            const baseInfo = 'Если через 15 минут не удастся зачислить деньги на ваш лицевой счёт, вернём их вам на карту.';
            const text = (bonuses && isUseBonuses)
                ? isBonusesSuccess
                    ? `Баланс будет пополнен на ${cardPaymentSum} ${RubleSymbol}. С бонусного счета списали ${bonusesWithText}. ${baseInfo}`
                    : `${baseInfo} Ещё нам не удалось списать ${bonusesWithText} с бонусного счета. `
                : baseInfo;

            return {
                type: EResultPopupStatus.DEFAULT,
                title: 'Платёж в обработке',
                text,
                isNeedSupport: (isUseBonuses && !isBonusesSuccess),
            };
        }
    }, [agreement, queryAgreement, bonusesWithText, isBonusesSuccess, isUseBonuses, bonuses, cardPaymentSum, payStatus, paymentSum]);

    const setCardPaymentResult = useCallback((): IPopupData => {
        return (isPayCardSuccess && (payStatus === 1 || payStatus === 2))
            ? setPaymentResultByStatus()
            : {
                type: EResultPopupStatus.ERROR,
                title: (bonuses && !isBonusesSuccess) ? 'Платёж не прошёл, бонусы не списали' : 'Оплата не прошла',
                // eslint-disable-next-line max-len
                text: (bonuses && isBonusesSuccess) ? 'К сожалению, бонусами не получится оплатить оборудование. Но не пугайтесь, они никуда не пропали. Мы только что перенесли ваши бонусы на счёт договора. Теперь вы можете оплатить покупку. ' : '',
                isNeedSupport: true,
                isBonusesPaymentSuccessfull: isBonusesSuccess,
            };
    }, [isBonusesSuccess, isPayCardSuccess, payStatus, bonuses, setPaymentResultByStatus]);

    const setBonusesPaymentResult = useCallback((): IPopupData => {
        return isBonusesSuccess
            ? {
                type: EResultPopupStatus.SUCCESS,
                title: `Вы оплатили услуги по договору ${queryAgreement || agreement}`,
                text: `Баланс пополнен на ${bonuses} ${RubleSymbol} — вы оплатили всю сумму бонусами.`,
            }
            : {
                type: EResultPopupStatus.ERROR,
                title: 'Платёж не прошёл, бонусы не списали. ',
                isNeedSupport: true,
            };
    }, [agreement, queryAgreement, isBonusesSuccess, bonuses]);

    const setAutoPayConnectResult = useCallback((): IPopupData => {
        return isAutoPaySuccess
            ? {
                type: EResultPopupStatus.SUCCESS,
                title: 'Вы подключили автоплатёж',
            }
            : {
                type: EResultPopupStatus.ERROR,
                title: 'Не удалось подключить автоплатёж. ',
                isNeedSupport: true,
            };
    }, [isAutoPaySuccess]);

    switch (true) {
        case !!cardPaymentSum:
            return setCardPaymentResult();
        case !!bonusesValue:
            return setBonusesPaymentResult();
        case (!cardPaymentSum && !bonuses && isConnectAutoPay):
            return setAutoPayConnectResult();
        default:
            return defaultPopupData;
    }
};
